import { Immutable } from "immer";
import React, { useEffect, useRef } from "react";
import { formatDistance } from "tsi-common-react/src/apps/retail/helpers";
import {
    RetailStoreEventCTAType,
    RetailStoreEventType,
} from "tsi-common-react/src/constants";
import { useMountEffect } from "tsi-common-react/src/hooks/useMountEffect";
import { ILocation, RetailStore } from "tsi-common-react/src/models/location";
import {
    Latitude,
    Longitude,
    Meters,
    isoLatitude,
    isoLongitude,
    isoSyncStoreID,
} from "tsi-common-react/src/models/nominals";
import { preferredLocationSelector } from "tsi-common-react/src/store/common/selectors";
import { useAppDispatch } from "tsi-common-react/src/store/reducers";
import { trackRetailStoreEvent } from "tsi-common-react/src/utils/analytics";
import {
    DistanceUnit,
    getDistanceUnit,
} from "tsi-common-react/src/utils/distance";
import { formatPhoneNumber } from "tsi-common-react/src/utils/format";
import {
    getGoogleLatLng,
    haversineDistanceMeters,
    loadGMaps,
    newMap,
} from "tsi-common-react/src/utils/maps";
import { useStaticURL } from "tsi-common-react/src/utils/urls";
import { t } from "ttag";

import { templates } from "@reactivated";

import { getDocument } from "@thelabnyc/thelabui/src/utils/dom";

import iconMarker2 from "../../../assets/img/retail-locator/icon-marker2.png";
import { useAppSelector } from "../../../hooks";
import { HolidayHoursSummary } from "../HolidayHoursSummary";
import { loadStoreByExternalID } from "../thunks";

type IProps = Immutable<templates.FlagshipInfoTemplate>;

function getFlagshipLatLng(
    lat: number | null,
    lng: number | null,
): { lat: Latitude; lng: Longitude } | null {
    if (lat == null || lng == null) {
        return null;
    }
    return {
        lat: isoLatitude.wrap(String(lat)),
        lng: isoLongitude.wrap(String(lng)),
    };
}

async function initializeMap(
    mapElement: HTMLDivElement,
    storeLocation: { lat: Latitude; lng: Longitude },
): Promise<void> {
    try {
        await loadGMaps();
    } catch (e) {
        console.warn("Google Maps failed to load", e);
        return;
    }
    const position = getGoogleLatLng(storeLocation);
    const retailMap = newMap(mapElement, {
        center: position,
        zoom: 16,
        scrollwheel: false,
        draggable: true,
        disableDefaultUI: false,
        clickableIcons: false,
        mapTypeId: google.maps.MapTypeId.ROADMAP,
    });
    const marker = new google.maps.Marker({
        position: position,
        icon: iconMarker2,
        title: t`Tempur-Pedic Store`,
    });
    marker.setMap(retailMap);
}

function computeMapDistance(
    storeLocation: { lat: Latitude; lng: Longitude } | null,
    location: Immutable<ILocation> | null,
): Meters | null {
    if (!storeLocation) {
        return null;
    }
    if (!location || !("lat" in location) || !("lng" in location)) {
        return null;
    }
    const userLocation: { lat: Latitude; lng: Longitude } = {
        lat: location.lat,
        lng: location.lng,
    };
    const userLatLng = getGoogleLatLng(userLocation);
    if (!Number.isFinite(userLatLng.lat) || !Number.isFinite(userLatLng.lng)) {
        return null;
    }
    return haversineDistanceMeters(storeLocation, userLocation);
}

function handleLocationInfoClick(
    store: Immutable<RetailStore> | null,
    ctaType: RetailStoreEventCTAType,
    textContent: string | null,
    event: React.MouseEvent<HTMLElement>,
): void {
    event.stopPropagation();
    const element = event.currentTarget;
    const linkText = textContent || element.textContent?.trim() || "";
    const linkUrl = element.getAttribute("href");
    trackRetailStoreEvent(
        RetailStoreEventType.TEMPUR_STORE,
        store,
        ctaType,
        linkText,
        linkUrl,
    );
}

export const FlagshipStoreInfoMapSSR = (props: IProps): React.JSX.Element => {
    const staticURL = useStaticURL();
    const mapRef = useRef<HTMLDivElement | null>(null);
    const dispatch = useAppDispatch();
    const location = useAppSelector(preferredLocationSelector);
    const storeID = isoSyncStoreID.wrap(props.store_id);
    const store =
        useAppSelector((state) =>
            state.retail.stores.find((s) => s.external_id === storeID),
        ) || null;

    const storeLocation = getFlagshipLatLng(props.lat, props.lng);
    const mapDistance = computeMapDistance(storeLocation, location);

    // Fall back to the API-reported distance for ZIP-only locations,
    // where we can't compute haversine without client-side lat/lng.
    const displayDistance: Meters | null =
        mapDistance ?? store?.distance ?? null;

    useEffect(() => {
        if (!location) {
            return;
        }
        void dispatch(loadStoreByExternalID({ location, storeID }));
    }, [location, storeID, dispatch]);

    // Initialize Google Map
    useMountEffect(() => {
        if (storeLocation && mapRef.current) {
            void initializeMap(mapRef.current, storeLocation);
        }
    });

    // Attach booking button tracking
    useEffect(() => {
        if (!store) {
            return;
        }
        const controller = new AbortController();
        const document = getDocument();
        if (!document) {
            return;
        }
        const buttons =
            document.querySelectorAll<HTMLAnchorElement>("a[class='button']");
        buttons.forEach((element) => {
            const linkText = element.textContent?.trim();
            if (
                linkText &&
                linkText.toLocaleLowerCase() ===
                    t`book appointment`.toLocaleLowerCase()
            ) {
                const linkUrl = element.getAttribute("href");
                element.addEventListener(
                    "click",
                    () => {
                        trackRetailStoreEvent(
                            RetailStoreEventType.TEMPUR_STORE,
                            store,
                            RetailStoreEventCTAType.APPOINTMENT,
                            linkText,
                            linkUrl,
                        );
                    },
                    { signal: controller.signal },
                );
            }
        });
        return () => controller.abort();
    }, [store]);

    const phoneFormatted = props.phone ? formatPhoneNumber(props.phone) : "";
    const destinationAddress = encodeURIComponent(
        `${props.store_name} ${props.address} ${props.address2} ${props.city},${props.state} ${props.postal}`,
    );

    return (
        <section className="location l-capped-width">
            <div className="location__info-wrapper">
                <div className="location__distance">
                    <div className="location__distance-text">
                        <span className="location__distance-text--large">
                            {displayDistance && formatDistance(displayDistance)}
                        </span>
                        <br />
                        <span className="location__distance-text--small">
                            {displayDistance &&
                                (getDistanceUnit() === DistanceUnit.KILOMETERS
                                    ? t`km`
                                    : t`miles`)}
                        </span>
                    </div>
                </div>
                <div className="location__info">
                    <h3 className="location__header">
                        <span className="location__name1">{props.title}</span>
                        <span className="location__name2">
                            {props.shopping_district}
                        </span>
                    </h3>
                    <div className="location__location">
                        <span className="location__icon">
                            <img
                                alt={t`Location`}
                                src={`${staticURL}img/retail-locator/location-icon.png`}
                            />
                        </span>
                        <div className="location__address">
                            <div className="location__address">
                                {props.address}
                                <br />
                                {props.city}, {props.state} {props.postal}
                            </div>
                        </div>
                        <div className="location__nearby">
                            {props.located_near}
                        </div>
                    </div>
                    <div className="location__contact">
                        <div className="location__item location__hours">
                            <span className="location__icon">
                                <img
                                    alt={t`Hours`}
                                    src={`${staticURL}img/retail-locator/icon-clock.png`}
                                />
                            </span>
                            <span className="location__tooltip-trigger">
                                {props.hours_summary_abbr
                                    .split(",")
                                    .map((dayHours, dayIndex) => {
                                        const periods = dayHours.split(" & ");
                                        return (
                                            <React.Fragment
                                                key={`day-${dayIndex}`}
                                            >
                                                {periods.map(
                                                    (period, periodIndex) =>
                                                        periodIndex === 0 ? (
                                                            <span
                                                                key={`period-${periodIndex}`}
                                                            >
                                                                {period}
                                                            </span>
                                                        ) : (
                                                            <span
                                                                key={`period-${periodIndex}`}
                                                                className="location__hours-segment"
                                                            >
                                                                & {period}
                                                            </span>
                                                        ),
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                            </span>
                        </div>
                        <div className="location__item location__directions">
                            <span className="location__icon">
                                <img
                                    alt={t`Directions`}
                                    src={`${staticURL}img/retail-locator/icon-driving.png`}
                                />
                            </span>
                            <a
                                href={`https://www.google.com/maps/dir/?api=1&destination=${destinationAddress}`}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(event) =>
                                    handleLocationInfoClick(
                                        store,
                                        RetailStoreEventCTAType.MAP_DIRECTIONS,
                                        t`Get icon directions`,
                                        event,
                                    )
                                }
                            >
                                {t`Directions`}
                            </a>
                        </div>
                        {props.phone && (
                            <div className="location__item location__phone">
                                <span className="location__icon">
                                    <img
                                        alt={t`Phone Number`}
                                        src={`${staticURL}img/retail-locator/icon-phone.png`}
                                    />
                                </span>
                                <a
                                    href={`tel:${props.phone}`}
                                    title={t`Phone Number`}
                                    onClick={(event) =>
                                        handleLocationInfoClick(
                                            store,
                                            RetailStoreEventCTAType.CALL,
                                            null,
                                            event,
                                        )
                                    }
                                >
                                    {phoneFormatted}
                                </a>
                            </div>
                        )}
                    </div>
                    <HolidayHoursSummary
                        className="location__holiday-hours"
                        lines={props.upcoming_holiday_hours_summary}
                    />
                </div>
            </div>
            {props.lat != null && props.lng != null && (
                <div className="location__map" id="map_canvas" ref={mapRef} />
            )}
        </section>
    );
};
